import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Language } from 'src/store/language/languageStore.types';
import {
  useAction,
  useBaseUrlStore,
  useLabels,
  useLanguageStore,
  useWindowWidthStore,
} from 'src/StoreDataProvider';
import { HeaderProps } from './header.types';
import desktopLogo from '../../../assets/kfw_logo_1280-2x.svg';
import mobileLogo from '../../../assets/kfw_logo_320-2x.svg';

const Header: (props: HeaderProps) => JSX.Element = props => {
  const {
    setLanguage,
    setFirstLanguageChangeDone,
    setBaseUrlLanguage,
    initBaseUrl,
    getLanguageByPath,
    initializeLabels,
  } = useAction();
  const { language, firstLanguageChangeDone } = useLanguageStore();
  const baseUrlStore = useBaseUrlStore();
  const labels = useLabels();
  const windowState = useWindowWidthStore();

  const overlayRef = useRef<HTMLDivElement>(null);
  const overlayAnchorStartRef = useRef<HTMLAnchorElement>(null);
  const toHomePageRef = useRef<HTMLAnchorElement>(null);
  const mainAnchorRef = useRef<HTMLAnchorElement>(null);

  const [triggerLanguageChange, setTriggerLanguageChange] = useState(false);
  const [initBaseUrlChangeDone, setInitBaseUrlChangeDone] = useState(false);
  const [menuOverlayVisible, setMenuOverlayVisible] = useState(false);

  const params = useParams();

  const [headerItems] = useState(new Map<string, string[]>());

  const headerLinks = [
    [
      'https://www.kfw-entwicklungsbank.de/Internationale-Finanzierung/KfW-Entwicklungsbank/Aufgaben-und-Ziele/',
      'https://www.kfw-entwicklungsbank.de/International-financing/KfW-Development-Bank/Tasks-and-goals/',
    ],
    [
      'https://www.kfw-entwicklungsbank.de/Internationale-Finanzierung/KfW-Entwicklungsbank/Themen/',
      'https://www.kfw-entwicklungsbank.de/International-financing/KfW-Entwicklungsbank/Topics/',
    ],
    [
      'https://www.kfw-entwicklungsbank.de/Internationale-Finanzierung/KfW-Entwicklungsbank/Weltweite-Pr%C3%A4senz/',
      'https://www.kfw-entwicklungsbank.de/International-financing/KfW-Development-Bank/Local-presence/',
    ],
    [
      'https://www.kfw-entwicklungsbank.de/Internationale-Finanzierung/KfW-Entwicklungsbank/Unsere-Wirkungen/',
      'https://www.kfw-entwicklungsbank.de/International-financing/KfW-Development-Bank/Our-results/',
    ],
    [
      'https://www.kfw-entwicklungsbank.de/Internationale-Finanzierung/KfW-Entwicklungsbank/Transparenz/',
      'https://www.kfw-entwicklungsbank.de/International-financing/KfW-Development-Bank/Transparency/',
    ],
    [
      'https://www.kfw-entwicklungsbank.de/Internationale-Finanzierung/KfW-Entwicklungsbank/Service/index-2.html',
      'https://www.kfw-entwicklungsbank.de/International-financing/KfW-Development-Bank/Service/',
    ],
    ['https://www.kfw.de/stories/', 'https://www.kfw.de/stories/index-en.html'],
    ['https://www.kfw.de/%C3%9Cber-die-KfW/', 'https://www.kfw.de/About-KfW/'],
  ];

  useEffect(() => {
    if (firstLanguageChangeDone) {
      initializeLabels(language);
      setBaseUrlLanguage(language);
    } else {
      setFirstLanguageChangeDone(true);
    }
  }, [language, triggerLanguageChange]);

  useEffect(() => {
    if (params.base && initBaseUrlChangeDone) {
      window.open(
        window.location.href.replaceAll(params.base, baseUrlStore.path),
        '_self'
      );
    } else {
      setInitBaseUrlChangeDone(true);
    }
  }, [baseUrlStore.language]);

  useEffect(() => {
    if (params.base) {
      // if (!firstLanguageChangeDone) {
      initBaseUrl(params.base);
      setLanguage(getLanguageByPath(params.base));
      if (getLanguageByPath(params.base) === language) {
        setTriggerLanguageChange(true);
      }
      // }
    }
  }, [params.base]);

  useEffect(() => {
    headerItems.clear();
    let count = 1;
    while (labels[`headerTitle${count}`]) {
      const headerLinkItem = headerLinks[count - 1];
      if (headerLinkItem) {
        const headerLinkItemValue =
          headerLinkItem[language === Language.DE ? 0 : 1];
        if (headerLinkItemValue) {
          headerItems.set(labels[`headerTitle${count}`], [
            headerLinkItemValue,
            labels[`headerTitleAddition${count}`],
          ]);
        }
      }
      count++;
    }
  }, [labels]);

  useEffect(() => {
    const bodyElements = document.getElementsByTagName('body');
    if (bodyElements.length > 0) {
      menuOverlayVisible
        ? bodyElements[0].classList.add('prevent-scroll')
        : bodyElements[0].classList.remove('prevent-scroll');
      if (menuOverlayVisible) {
        if (overlayAnchorStartRef.current) {
          overlayAnchorStartRef.current.focus();
        }
      } else {
        if (document.getElementById('main-content-wrapper')) {
          document.getElementById('main-content-wrapper')?.focus();
        }
      }
    }
  }, [menuOverlayVisible]);

  useEffect(() => {
    setMenuOverlayVisible(false);
  }, [windowState.isMobileView]);

  function closeMobileMenu(): void {
    if (overlayRef.current) {
      overlayRef.current.classList.add('menu__overlay__fadeout');
      setTimeout(() => {
        setMenuOverlayVisible(false);
        if (overlayRef.current) {
          overlayRef.current.classList.remove('menu__overlay__fadeout');
        }
      }, 195);
    } else {
      setMenuOverlayVisible(false);
    }
  }

  return (
    <>
      <header id="header">
        {menuOverlayVisible ? (
          <div className="menu__overlay" ref={overlayRef}>
            <a
              id="overlay-anchor-start"
              className="anchor"
              tabIndex={-1}
              ref={overlayAnchorStartRef}
            ></a>
            <div className="nav-mobile__inner js-nav-mobile-inner">
              <button
                className="button nav-mobile__close js-nav-mobile-close"
                title={labels.close}
                aria-label={labels.close}
              >
                <span
                  className="icon-schliessen"
                  aria-hidden="true"
                  onKeyDown={e => {
                    if (e.key === 'Enter') closeMobileMenu();
                  }}
                  onClick={() => {
                    closeMobileMenu();
                  }}
                ></span>
              </button>
              <div className="nav-mobile__nav">
                <div
                  className="nav-mobile__breadcrumb l-container"
                  data-module="mobile-breadcrumb"
                >
                  <a
                    data-section="main-navi_home_mobile"
                    className="nav-mobile__home-link js-nav-mobile-homelink"
                    href={
                      language === Language.DE
                        ? 'https://www.kfw-entwicklungsbank.de/Internationale-Finanzierung/KfW-Entwicklungsbank/'
                        : 'https://www.kfw-entwicklungsbank.de/International-financing/KfW-Entwicklungsbank/'
                    }
                    title={labels.homePage}
                    aria-label={labels.homePage}
                    ref={toHomePageRef}
                  >
                    <span className="icon-home" aria-hidden="true"></span>
                    <span className="link__name">{labels.homePage}</span>
                  </a>
                </div>
                <div className="nav-mobile__scroll">
                  <div className="nav-mobile__login-outer">
                    <div className="nav-mobile__login-wrapper js-login-wrapper">
                      <nav className="nav-mobile__holder" role="navigation">
                        <div className="nav-mobile__nav-inner js-nav-mobile-list">
                          <ul className="nav-mobile__list">
                            {Array.from(headerItems.entries()).map(entry => (
                              <li
                                className="nav-mobile__item nav-mobile__item--children"
                                key={`header-item-mobile-${entry[0]}`}
                              >
                                <a
                                  className="link--notrack js-nav-mobile-link nav-mobile__link"
                                  href={entry[1][0]}
                                >
                                  <div className="nav-mobile__link__title">
                                    <div className="nav-mobile__link__title__text">
                                      {entry[0]}
                                    </div>
                                    <em
                                      className="icon-pfeil-rechts"
                                      aria-hidden="true"
                                    />
                                  </div>
                                  {entry[1][1] ? (
                                    <>
                                      <br />
                                      <small className="nav-mobile__subline">
                                        {entry[1][1]}
                                      </small>
                                    </>
                                  ) : (
                                    ''
                                  )}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </nav>
                      <div className="nav-mobile__bottom">
                        <ul className="header__meta switch-box">
                          <li className="header__meta-link">
                            <button
                              onClick={() => {
                                setLanguage(
                                  language === Language.DE ? 'en-EN' : 'de-DE'
                                );
                              }}
                              className="switch-language-mobile"
                            >
                              {language === Language.DE ? 'English' : 'Deutsch'}
                            </button>
                          </li>
                          {language === Language.DE ? (
                            <li className="header__meta-link">
                              <a
                                href="https://www.kfw.de/inlandsfoerderung/Gebaerdensprache.html"
                                className="sign-language-mobile"
                                aria-label="Im folgenden Video erklären wir Ihnen Inhalt und Navigation des KfW-Webauftritts"
                                title="Gebärdensprache"
                              >
                                <span
                                  className="icon-gebaerdensprache"
                                  aria-hidden="true"
                                />
                              </a>
                            </li>
                          ) : (
                            ''
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <a
              href=""
              id="overlay-anchor-end"
              className="anchor"
              onFocus={() => {
                if (toHomePageRef.current) {
                  toHomePageRef.current.focus();
                }
              }}
            ></a>
          </div>
        ) : (
          ''
        )}
        <div className="to-content-container">
          <div className="to-content-container-inner">
            {!windowState.isMobileView ? (
              <button
                className="header__skip-to-content"
                data-module="skip-navigation"
                onClick={() => {
                  if (mainAnchorRef.current) {
                    mainAnchorRef.current.focus();
                  }
                  if (document.getElementById('sticky-header')) {
                    window.scrollTo({
                      top: document.getElementById('sticky-header')?.offsetTop,
                    });
                  }
                }}
              >
                {labels.skipToMainContent}
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className="l-container l-container-full-width l-container__first-part">
          <div className="l-fullwidth">
            <div
              id="headerContent"
              className="header content-padding-default header__fixed"
            >
              <button
                className="header__menu-button"
                id="nav-mobile-button"
                aria-expanded="false"
                aria-label={labels.menu}
                aria-controls="nav-mobile"
                data-module="mobile-navigation-button-master"
                onClick={() => setMenuOverlayVisible(true)}
              >
                <span
                  className={
                    baseUrlStore.language === Language.DE
                      ? 'icon-menu-texthinweis'
                      : 'icon-menu-texthinweis_en'
                  }
                  aria-hidden="true"
                ></span>
              </button>
              <div className="header__logo">
                <a
                  className="header__logo__link"
                  href={
                    language === Language.DE
                      ? 'https://www.kfw-entwicklungsbank.de/Internationale-Finanzierung/KfW-Entwicklungsbank/'
                      : 'https://www.kfw-entwicklungsbank.de/International-financing/KfW-Entwicklungsbank/'
                  }
                >
                  <picture className="header__logo">
                    <img
                      src={desktopLogo}
                      alt={labels.routeToKfWHomepage}
                      className="pictureImg pictureImg__desktop"
                    />
                    <img
                      src={mobileLogo}
                      alt={labels.routeToKfWHomepage}
                      className="pictureImg pictureImg__mobile"
                    />
                  </picture>
                </a>
              </div>
              <ul className="header__meta switch-box">
                <li className="header__meta-link header__meta-link__desktop">
                  <button
                    onClick={() => {
                      setLanguage(language === Language.DE ? 'en-EN' : 'de-DE');
                    }}
                    style={{ cursor: 'pointer' }}
                    className="headerLink headerLink__language"
                  >
                    {language === Language.DE ? 'English' : 'Deutsch'}
                  </button>
                </li>
                {language === Language.DE ? (
                  <li className="header__meta-link header__meta-link__desktop">
                    <a
                      href="https://www.kfw.de/inlandsfoerderung/Gebaerdensprache.html"
                      className="headerLink headerLink__icon"
                      aria-label="Im folgenden Video erklären wir Ihnen Inhalt und Navigation des KfW-Webauftritts"
                      title="Gebärdensprache"
                    >
                      <span
                        className="icon-gebaerdensprache"
                        aria-hidden="true"
                      />
                    </a>
                  </li>
                ) : (
                  ''
                )}
              </ul>
            </div>
          </div>
        </div>
        <div id="sticky-header" className="sticky-header">
          <div className="l-container nav-primary__overlap">
            <nav className="nav-primary">
              <ul className="nav-primary__list">
                {Array.from(headerItems.entries()).map(entry => (
                  <li
                    className="nav-primary__listItem"
                    key={`header-item-${entry[0]}`}
                  >
                    <a
                      className="nav-primary__link"
                      href={entry[1][0]}
                      aria-label={entry[0]}
                    >
                      {entry[0]}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <div className="nav-primary__wrapper"></div>
        </div>
      </header>
      <a
        id="main-anchor"
        className="anchor"
        tabIndex={-1}
        ref={mainAnchorRef}
      ></a>
    </>
  );
};

export default Header;
